import { useRef, useState } from "react"
import { Check, AlertTriangle, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import type { GoNoGoAssessment, GngDecision } from "@/stores/strategy-store"
import {
  OUTCOME_LABELS,
  STAGE_LABELS,
  bidLifecycleOf,
  type LifecycleSource,
} from "@/lib/bid-lifecycle"
import { cn } from "@/lib/utils"
import { reviewCountPhrase } from "@/lib/compliance-position"
import { RECOMMENDATION_LABEL } from "@/lib/gng-score"

interface GoNoGoDecisionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  assessment: GoNoGoAssessment
  /** The bid as loaded: where it is decides whether the "move the bid" option appears. */
  currentBid: LifecycleSource | null
  onConfirm: (data: {
    decision: GngDecision
    decisionNotes: string
    decisionConditions: string
    transitionBidStatus: boolean
  }) => void
  isLoading?: boolean
  /**
   * Mandatory-risk summary for the soft-gate row. Titles (not just a count) so
   * the conditional-go conditions box can be seeded with what actually has to
   * be resolved. Optional — callers without a compliance matrix omit it.
   */
  compliance?: {
    mandatoryNonComplyTitles: string[]
    mandatoryNeedsReview: number
    /** Of `mandatoryNeedsReview`, how many read "Needs your confirmation". */
    mandatoryNeedsConfirmation?: number
  }
}

/**
 * Where the decision moves the bid (R4 plan §2.7, mirroring the backend):
 * Go and Conditional go → Active · Plan, No go → Closed · No go.
 */
function transitionLabelFor(decision: GngDecision): string {
  if (decision === 'no_go') return `Closed · ${OUTCOME_LABELS.no_go}`
  return `Active · ${STAGE_LABELS.plan}`
}

/** The move is offered only from a lead or an active bid at Qualify, as the backend allows. */
function canTransition(bid: LifecycleSource | null | undefined): boolean {
  if (bid == null) return false
  const { lifecycle, stage } = bidLifecycleOf(bid)
  return lifecycle === 'lead' || (lifecycle === 'active' && stage === 'qualify')
}

const options: { value: GngDecision; label: string; sublabel: string; icon: typeof Check; iconBg: string; iconColor: string }[] = [
  { value: 'go', label: RECOMMENDATION_LABEL.go, sublabel: 'Pursue this bid', icon: Check, iconBg: 'bg-[hsl(var(--status-success)/0.1)]', iconColor: 'text-[hsl(var(--status-success))]' },
  { value: 'conditional_go', label: RECOMMENDATION_LABEL.conditional_go, sublabel: 'Go, with conditions', icon: AlertTriangle, iconBg: 'bg-[hsl(var(--status-warning)/0.1)]', iconColor: 'text-[hsl(var(--status-warning))]' },
  { value: 'no_go', label: RECOMMENDATION_LABEL.no_go, sublabel: 'Do not pursue', icon: X, iconBg: 'bg-[hsl(var(--destructive)/0.1)]', iconColor: 'text-[hsl(var(--destructive))]' },
]

export function GoNoGoDecisionDialog({ open, onOpenChange, assessment, currentBid, onConfirm, isLoading, compliance }: GoNoGoDecisionDialogProps) {
  const [decision, setDecision] = useState<GngDecision>('go')
  const [notes, setNotes] = useState('')
  const [conditions, setConditions] = useState('')
  const [transitionStatus, setTransitionStatus] = useState(true)
  const conditionsTouched = useRef(false)

  const recLabel = assessment.recommendation ? RECOMMENDATION_LABEL[assessment.recommendation] : null

  const targetLabel = transitionLabelFor(decision)
  const showTransitionOption = canTransition(currentBid)

  const nonComplyTitles = compliance?.mandatoryNonComplyTitles ?? []
  const needsReview = compliance?.mandatoryNeedsReview ?? 0
  const needsConfirmation = compliance?.mandatoryNeedsConfirmation ?? 0
  const atRisk = nonComplyTitles.length + needsReview
  const showComplianceWarning = atRisk > 0 && decision !== 'no_go'

  /** Named conditions beat a vague one: the first three, then a count. */
  const conditionsSeed =
    nonComplyTitles.length > 0
      ? `Subject to resolving: ${nonComplyTitles.slice(0, 3).join('; ')}${
          nonComplyTitles.length > 3 ? `; +${nonComplyTitles.length - 3} more` : ''
        }`
      : null

  // Seeding happens on the click, not in an effect: an effect keyed on a prop
  // array reruns on every parent render, and re-seeding after the user edits
  // would silently rewrite their record of the decision.
  const handleSelectDecision = (value: GngDecision) => {
    setDecision(value)
    if (value === 'conditional_go' && !conditionsTouched.current && conditions === '' && conditionsSeed) {
      setConditions(conditionsSeed)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Decision</DialogTitle>
          <DialogDescription>Make your Go / No-Go decision for this bid opportunity.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Score summary */}
          <div className="bg-muted/30 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm">Assessment score</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{assessment.overallScore ?? 0} / 100</span>
              {assessment.recommendation && (
                <Badge variant="secondary" className="text-xs">Recommended: {recLabel}</Badge>
              )}
            </div>
          </div>

          {/* Decision options */}
          <div>
            <label className="text-sm font-medium block mb-2">Your decision</label>
            <div className="grid grid-cols-3 gap-2">
              {options.map(opt => {
                const Icon = opt.icon
                const selected = decision === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelectDecision(opt.value)}
                    className={cn(
                      "border-2 rounded-xl p-3 text-center transition-colors",
                      selected ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.05)]" : "border-border hover:bg-muted/30"
                    )}
                  >
                    <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center mx-auto mb-2", opt.iconBg)}>
                      <Icon className={cn("h-4 w-4", opt.iconColor)} />
                    </div>
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{opt.sublabel}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Soft-gate: inform, never block (house philosophy, spec §6). */}
          {showComplianceWarning && (
            <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--destructive)/0.06)] p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--destructive))]" />
              <p className="text-meta">
                {nonComplyTitles.length > 0 && (
                  <span className="font-medium text-[hsl(var(--destructive))]">
                    {nonComplyTitles.length} mandatory non-comply
                  </span>
                )}
                {nonComplyTitles.length > 0 && needsReview > 0 && ' · '}
                {needsReview > 0 && (
                  <span className="font-medium text-[hsl(var(--status-warning))]">
                    {needsReview} {reviewCountPhrase(needsReview, needsConfirmation, 'need review')}
                  </span>
                )}
                {' — recording this decision does not resolve them.'}
              </p>
            </div>
          )}

          {/* Conditions (for conditional_go) */}
          {decision === 'conditional_go' && (
            <div>
              <label className="text-sm font-medium block mb-1.5">Conditions</label>
              <textarea
                className="w-full border border-border rounded-lg p-3 text-sm bg-transparent resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                rows={2}
                placeholder='e.g., "Subject to partnering agreement with Acme Marine"'
                value={conditions}
                onChange={(e) => {
                  conditionsTouched.current = true
                  setConditions(e.target.value)
                }}
              />
            </div>
          )}

          {/* Decision notes */}
          <div>
            <label className="text-sm font-medium block mb-1.5">Decision notes</label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-transparent resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              rows={3}
              placeholder="Capture the rationale behind this decision..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Move the bid: shown for any decision while the bid is a lead or
              at Qualify (Go → Active · Plan, No go → Closed · No go). */}
          {showTransitionOption && (
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox
                checked={transitionStatus}
                onCheckedChange={(checked) => setTransitionStatus(checked === true)}
              />
              <span className="text-sm">
                Move the bid to <strong>{targetLabel}</strong>
              </span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onConfirm({
              decision,
              decisionNotes: notes,
              decisionConditions: conditions,
              transitionBidStatus: showTransitionOption && transitionStatus,
            })}
            disabled={isLoading}
          >
            {isLoading ? 'Recording...' : 'Confirm Decision'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
